import React from 'react';
import Actions from '../../flux/Actions';

interface UserMenuProps {
   menuRef: React.RefObject<HTMLDivElement>;
   menuOpen: boolean;
   onToggle: () => void;
   onClose: () => void;
   selectedProjectId: string | null;
   onExport: () => void;
   onImport: () => void;
   isImporting: boolean;
   variant?: 'icon' | 'footer';
   userName?: string | null;
   userEmail?: string | null;
}

function UserMenu({
   menuRef,
   menuOpen,
   onToggle,
   onClose,
   selectedProjectId,
   onExport,
   onImport,
   isImporting,
   variant = 'icon',
   userName,
   userEmail,
}: UserMenuProps) {
   const menuPosition =
      variant === 'footer' ? 'absolute bottom-full right-0 mb-2' : 'absolute right-0 mt-2';

   return (
      <div className={variant === 'footer' ? 'relative w-full' : 'relative'} ref={menuRef}>
         {variant === 'footer' ? (
            <div className="no-drag flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.045] p-2">
               <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white/10 text-[12px] font-semibold uppercase text-pearl">
                  {(userName || userEmail || 'U').slice(0, 1)}
               </div>
               <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-medium leading-4 text-pearl">
                     {userName || userEmail || 'User'}
                  </p>
                  {userEmail && (
                     <p className="truncate text-[11px] leading-4 text-silver/70">{userEmail}</p>
                  )}
               </div>
               <button
                  className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-silver transition-colors hover:bg-white/10 hover:text-pearl"
                  onClick={onToggle}
                  type="button"
                  title="Settings"
                  aria-label="Settings"
               >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.8}
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                     />
                     <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.8}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                     />
                  </svg>
               </button>
            </div>
         ) : (
            <button
               className="no-drag flex items-center justify-center p-2 text-silver rounded transition-colors"
               onClick={onToggle}
               type="button"
            >
               <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
               </svg>
            </button>
         )}

         {menuOpen && (
            <div
               className={`${menuPosition} w-48 bg-midnight border border-charcoal rounded shadow-xl overflow-hidden z-50 no-drag`}
            >
               {selectedProjectId && (
                  <button
                     onClick={onExport}
                     className="w-full text-left px-4 py-2 text-xs font-mono text-silver hover:bg-charcoal transition-colors"
                     type="button"
                  >
                     Export Project…
                  </button>
               )}
               <button
                  onClick={onImport}
                  disabled={isImporting}
                  className="w-full text-left px-4 py-2 text-xs font-mono text-silver hover:bg-charcoal transition-colors disabled:opacity-50"
                  type="button"
               >
                  {isImporting ? 'Importing…' : 'Import Project…'}
               </button>
               <button
                  onClick={() => {
                     onClose();
                     Actions.showModal('settings');
                  }}
                  className="w-full text-left px-4 py-2 text-xs font-mono text-silver hover:bg-charcoal transition-colors"
               >
                  Settings
               </button>
               <button
                  onClick={() => {
                     onClose();
                     Actions.logout();
                  }}
                  className="w-full text-left px-4 py-2 text-xs font-mono text-rose hover:bg-blood/20 transition-colors"
               >
                  Logout
               </button>
            </div>
         )}
      </div>
   );
}

export default UserMenu;
